/* =========================================================
   DISTANT TRAVELER
   SHARED MUSIC PLAYER + REAL AUDIO ANALYZER

   ONE AUDIO ENGINE FOR:
   - Hero PLAY NOW
   - Music Archive
   - Bottom Player

   KNOB:
   - controls meter COLOR only
   - does NOT control volume

   CASSETTE:
   - static
========================================================= */

(() => {
  const VERSION = "9.0";

  window.__DT_ACTIVE_PLAYER_VERSION = VERSION;


  /* =========================================================
     HELPERS
  ========================================================= */

  function clamp(value, min, max) {
    return Math.min(
      max,
      Math.max(min, value)
    );
  }


  function normalizeURL(url) {
    try {
      return new URL(
        url,
        window.location.href
      ).href;
    } catch {
      return url || "";
    }
  }


  function formatTime(seconds) {
    if (!Number.isFinite(seconds)) {
      return "00:00";
    }

    const minutes =
      Math.floor(seconds / 60);

    const secs =
      Math.floor(seconds % 60);

    return (
      String(minutes).padStart(2, "0") +
      ":" +
      String(secs).padStart(2, "0")
    );
  }


  function getUI() {
    return document.querySelector(
      "[data-dt-player-ui]"
    );
  }


  function getHeroButton() {
    return document.querySelector(
      "[data-dt-hero-play]"
    );
  }


  function getArchiveButtons() {
    return Array.from(
      document.querySelectorAll(
        "[data-dt-archive-play]"
      )
    );
  }


  function getPlayableArchiveButtons() {
    return getArchiveButtons().filter(
      button =>
        Boolean(
          button.dataset.audio
        )
    );
  }


  /* =========================================================
     SHARED PLAYER
  ========================================================= */

  function getPlayer() {
    const existing =
      window.DistantTravelerPlayer;


    if (
      !existing ||
      existing.version !== VERSION
    ) {
      try {
        existing?.audio?.pause();
      } catch (error) {
        console.warn(error);
      }


      const audio =
        new Audio();


      /*
        Must be assigned before MP3 src.
      */

      audio.crossOrigin =
        "anonymous";

      audio.preload =
        "metadata";


      window.DistantTravelerPlayer = {
        version: VERSION,

        audio,

        currentTrack: null,
        currentButton: null,
        currentIndex: -1,

        shuffleOn: false,
        repeatOn: false,

        /*
          0.00 = yellow
          0.25 = orange
          0.50 = red
          0.75 = purple
          1.00 = blue
        */

        meterColor: 0,

        audioContext: null,
        mediaSource: null,
        analyser: null,
        frequencyData: null,

        levels:
          new Array(8).fill(0),

        animationFrame: null,

        audioEventsBound: false
      };
    }


    return window.DistantTravelerPlayer;
  }


  /* =========================================================
     FREQUENCY BANDS
  ========================================================= */

  const frequencyBands = [
    [20, 80],
    [80, 180],
    [180, 400],
    [400, 900],
    [900, 2000],
    [2000, 4500],
    [4500, 9000],
    [9000, 16000]
  ];


  const frequencyGain = [
    1.12,
    1.10,
    1.12,
    1.16,
    1.24,
    1.38,
    1.58,
    1.90
  ];


  /* =========================================================
     WEB AUDIO ANALYZER
  ========================================================= */

  async function ensureAnalyser() {
    const player =
      getPlayer();


    try {
      const AudioContextClass =
        window.AudioContext ||
        window.webkitAudioContext;


      if (!AudioContextClass) {
        console.warn(
          "Distant Traveler: Web Audio API unavailable."
        );

        return false;
      }


      if (!player.audioContext) {
        player.audioContext =
          new AudioContextClass();
      }


      if (!player.mediaSource) {
        player.mediaSource =
          player.audioContext
            .createMediaElementSource(
              player.audio
            );
      }


      if (!player.analyser) {
        player.analyser =
          player.audioContext
            .createAnalyser();


        player.analyser.fftSize =
          2048;

        player.analyser.minDecibels =
          -95;

        player.analyser.maxDecibels =
          -18;

        player.analyser
          .smoothingTimeConstant =
          0.48;


        player.frequencyData =
          new Uint8Array(
            player.analyser
              .frequencyBinCount
          );


        /*
          AUDIO
            ↓
          ANALYZER
            ↓
          SPEAKERS
        */

        player.mediaSource.connect(
          player.analyser
        );


        player.analyser.connect(
          player.audioContext.destination
        );
      }


      if (
        player.audioContext.state ===
        "suspended"
      ) {
        await player.audioContext.resume();
      }


      return true;

    } catch (error) {
      console.error(
        "Distant Traveler analyzer error:",
        error
      );

      return false;
    }
  }


  /* =========================================================
     FREQUENCY ENERGY
  ========================================================= */

  function getBandEnergy(
    minFrequency,
    maxFrequency,
    bandIndex
  ) {
    const player =
      getPlayer();


    if (
      !player.analyser ||
      !player.frequencyData ||
      !player.audioContext
    ) {
      return 0;
    }


    const data =
      player.frequencyData;


    const binCount =
      player.analyser
        .frequencyBinCount;


    const nyquist =
      player.audioContext
        .sampleRate / 2;


    let start =
      Math.floor(
        (
          minFrequency /
          nyquist
        ) *
        binCount
      );


    let end =
      Math.ceil(
        (
          maxFrequency /
          nyquist
        ) *
        binCount
      );


    start =
      clamp(
        start,
        0,
        binCount - 1
      );


    end =
      clamp(
        end,
        start + 1,
        binCount
      );


    let total = 0;
    let peak = 0;
    let count = 0;


    for (
      let index = start;
      index < end;
      index++
    ) {
      const value =
        data[index] / 255;


      total += value;

      peak =
        Math.max(
          peak,
          value
        );

      count++;
    }


    if (!count) {
      return 0;
    }


    const average =
      total / count;


    let energy =
      average * 0.65 +
      peak * 0.35;


    if (energy < 0.018) {
      return 0;
    }


    energy =
      (
        energy -
        0.018
      ) /
      0.982;


    energy *=
      frequencyGain[bandIndex] ||
      1;


    energy =
      Math.pow(
        energy,
        0.65
      );


    return clamp(
      energy,
      0,
      1
    );
  }


  /* =========================================================
     COLOR PALETTE

     KNOB:
     YELLOW → ORANGE → RED → PURPLE → BLUE
  ========================================================= */

  function getMeterPalette() {
    const player =
      getPlayer();


    const amount =
      clamp(
        player.meterColor ?? 0,
        0,
        1
      );


    const stops = [
      {
        position: 0,
        color: [236, 190, 72]
      },
      {
        position: 0.25,
        color: [232, 124, 42]
      },
      {
        position: 0.50,
        color: [220, 65, 52]
      },
      {
        position: 0.75,
        color: [142, 72, 182]
      },
      {
        position: 1,
        color: [61, 118, 210]
      }
    ];


    let left =
      stops[0];

    let right =
      stops[1];


    for (
      let index = 0;
      index < stops.length - 1;
      index++
    ) {
      if (
        amount >=
          stops[index].position &&
        amount <=
          stops[index + 1].position
      ) {
        left =
          stops[index];

        right =
          stops[index + 1];

        break;
      }
    }


    const range =
      right.position -
      left.position;


    const localMix =
      range > 0
        ? (
            amount -
            left.position
          ) /
          range
        : 0;


    function interpolate(
      start,
      end
    ) {
      return Math.round(
        start +
        (
          end -
          start
        ) *
        localMix
      );
    }


    const r =
      interpolate(
        left.color[0],
        right.color[0]
      );


    const g =
      interpolate(
        left.color[1],
        right.color[1]
      );


    const b =
      interpolate(
        left.color[2],
        right.color[2]
      );


    const peakR =
      clamp(
        r + 25,
        0,
        255
      );


    const peakG =
      clamp(
        g + 18,
        0,
        255
      );


    const peakB =
      clamp(
        b + 18,
        0,
        255
      );


    return {
      active:
        `rgb(${r}, ${g}, ${b})`,

      glow:
        `rgba(${r}, ${g}, ${b}, 0.52)`,

      peak:
        `rgb(${peakR}, ${peakG}, ${peakB})`,

      peakGlow:
        `rgba(${peakR}, ${peakG}, ${peakB}, 0.78)`,

      off:
        `rgb(
          ${Math.round(r * 0.14)},
          ${Math.round(g * 0.14)},
          ${Math.round(b * 0.14)}
        )`
    };
  }


  /* =========================================================
     DRAW METER
  ========================================================= */

  function drawMeterColumn(
    column,
    level
  ) {
    if (!column) {
      return;
    }


    const bars =
      Array.from(
        column.querySelectorAll("i")
      );


    if (!bars.length) {
      return;
    }


    const palette =
      getMeterPalette();


    const litCount =
      level > 0.015
        ? Math.max(
            1,
            Math.round(
              level *
              bars.length
            )
          )
        : 0;


    bars.forEach(
      (bar, index) => {
        const active =
          index < litCount;


        const hot =
          active &&
          index >=
            bars.length - 2;


        if (!active) {
          bar.style.setProperty(
            "background",
            palette.off,
            "important"
          );

          bar.style.setProperty(
            "opacity",
            "0.4",
            "important"
          );

          bar.style.setProperty(
            "box-shadow",
            "none",
            "important"
          );

          return;
        }


        if (hot) {
          bar.style.setProperty(
            "background",
            palette.peak,
            "important"
          );

          bar.style.setProperty(
            "opacity",
            "1",
            "important"
          );

          bar.style.setProperty(
            "box-shadow",
            `0 0 7px ${palette.peakGlow}`,
            "important"
          );

          return;
        }


        bar.style.setProperty(
          "background",
          palette.active,
          "important"
        );


        bar.style.setProperty(
          "opacity",
          "1",
          "important"
        );


        bar.style.setProperty(
          "box-shadow",
          `0 0 5px ${palette.glow}`,
          "important"
        );
      }
    );
  }


  /* =========================================================
     RED SIGNAL LIGHTS
  ========================================================= */

  function updateLEDs(energy) {
    const ui =
      getUI();


    if (!ui) {
      return;
    }


    const LEDs =
      Array.from(
        ui.querySelectorAll(
          ".dt-status-lights i"
        )
      );


    const thresholds = [
      0.04,
      0.18,
      0.42
    ];


    LEDs.forEach(
      (LED, index) => {
        const active =
          energy >
          thresholds[index];


        const hot =
          active &&
          index === 2;


        if (!active) {
          LED.style.setProperty(
            "background",
            "#4b1c16",
            "important"
          );

          LED.style.setProperty(
            "opacity",
            "0.55",
            "important"
          );

          LED.style.setProperty(
            "box-shadow",
            "inset 0 0 2px #000",
            "important"
          );

          return;
        }


        if (hot) {
          LED.style.setProperty(
            "background",
            "#f25d38",
            "important"
          );

          LED.style.setProperty(
            "opacity",
            "1",
            "important"
          );

          LED.style.setProperty(
            "box-shadow",
            "0 0 6px rgba(242,93,56,1), 0 0 17px rgba(242,93,56,.55)",
            "important"
          );

          return;
        }


        LED.style.setProperty(
          "background",
          "#ca4932",
          "important"
        );


        LED.style.setProperty(
          "opacity",
          "1",
          "important"
        );


        LED.style.setProperty(
          "box-shadow",
          "0 0 5px rgba(220,72,47,.88), 0 0 11px rgba(220,72,47,.34)",
          "important"
        );
      }
    );
  }


  /* =========================================================
     REDRAW METERS
  ========================================================= */

  function redrawMeters() {
    const player =
      getPlayer();


    const ui =
      getUI();


    if (!ui) {
      return;
    }


    const columns =
      Array.from(
        ui.querySelectorAll(
          ".dt-meter-column"
        )
      );


    columns.forEach(
      (column, index) => {
        drawMeterColumn(
          column,
          player.levels[index] || 0
        );
      }
    );
  }


  /* =========================================================
     COLOR KNOB

     NO AUDIO VOLUME CONTROL
  ========================================================= */

  function setMeterColor(value) {
    const player =
      getPlayer();


    const amount =
      clamp(
        Number(value),
        0,
        1
      );


    player.meterColor =
      amount;


    const rotation =
      -130 +
      amount * 260;


    const ui =
      getUI();


    /*
      Supports both current and older
      knob markup.
    */

    const knob =
      ui?.querySelector(
        "[data-dt-color-knob], [data-dt-energy-knob], [data-dt-knob]"
      );


    const indicator =
      knob?.querySelector("span");


    if (indicator) {
      indicator.style.setProperty(
        "transform",
        `translateX(-50%) rotate(${rotation}deg)`,
        "important"
      );


      indicator.style.setProperty(
        "transform-origin",
        "50% 38px",
        "important"
      );
    }


    const palette =
      getMeterPalette();


    if (knob) {
      knob.style.setProperty(
        "border-color",
        palette.active,
        "important"
      );


      knob.style.setProperty(
        "box-shadow",
        `
          0 0 0 7px #10120e,
          0 0 0 8px ${palette.off},
          0 0 14px ${palette.glow}
        `,
        "important"
      );
    }


    redrawMeters();
  }

  function resetArchiveWaveforms() {
  document
    .querySelectorAll(".dt-archive-waveform i")
    .forEach((bar) => {
      bar.style.transform = "scaleY(1)";
      bar.style.opacity = "0.48";
      bar.style.background = "rgba(222, 214, 198, 0.28)";
      bar.style.boxShadow = "none";
    });
}


function updateActiveArchiveWaveform() {
  const player = getPlayer();

  const activeTrack = document.querySelector(
    ".dt-archive-track.is-playing-track"
  );

  if (!activeTrack) return;

  const bars = activeTrack.querySelectorAll(
    ".dt-archive-waveform i"
  );

  if (!bars.length) return;

  const palette = getMeterPalette();
  const levels = player.levels || [];

  bars.forEach((bar, index) => {
    const level = levels[index % levels.length] || 0;

    const scale = 0.7 + level * 1.15;
    const opacity = 0.45 + level * 0.55;

    bar.style.transform = `scaleY(${scale})`;
    bar.style.opacity = String(opacity);
    bar.style.background = level > 0.02 ? palette.active : palette.off;
    bar.style.boxShadow =
      level > 0.02 ? `0 0 6px ${palette.glow}` : "none";
  });
}

  /* =========================================================
     ANALYZER LOOP
  ========================================================= */

  function animateAudioHardware() {
    const player =
      getPlayer();


    const audio =
      player.audio;


    const ui =
      getUI();


    if (
      !ui ||
      !player.analyser ||
      !player.frequencyData
    ) {
      player.animationFrame =
        null;

      return;
    }


    if (!audio.paused) {
      player.analyser
        .getByteFrequencyData(
          player.frequencyData
        );
    }


    const columns =
      Array.from(
        ui.querySelectorAll(
          ".dt-meter-column"
        )
      );


    let averageEnergy = 0;
    let peakEnergy = 0;
    let stillMoving = false;


    frequencyBands.forEach(
      (band, index) => {
        const target =
          audio.paused
            ? 0
            : getBandEnergy(
                band[0],
                band[1],
                index
              );


        const previous =
          player.levels[index];


        /*
          Fast attack.
        */

        if (
          target >
          previous
        ) {
          player.levels[index] =
            previous +
            (
              target -
              previous
            ) *
            0.72;
        }


        /*
          Slower decay.
        */

        else {
          player.levels[index] =
            previous +
            (
              target -
              previous
            ) *
            0.08;
        }


        if (
          player.levels[index] <
          0.006
        ) {
          player.levels[index] =
            0;
        }


        const level =
          player.levels[index];


        averageEnergy +=
          level;


        peakEnergy =
          Math.max(
            peakEnergy,
            level
          );


        if (level > 0) {
          stillMoving =
            true;
        }


        if (columns[index]) {
          drawMeterColumn(
            columns[index],
            level
          );
        }
      }
    );


    averageEnergy /=
      frequencyBands.length;


    const overallEnergy =
      clamp(
        (
          averageEnergy * 0.45 +
          peakEnergy * 0.55
        ) *
        2,
        0,
        1
      );


    updateLEDs(
      overallEnergy
    );
    updateActiveArchiveWaveform();


    if (
      !audio.paused ||
      stillMoving
    ) {
      player.animationFrame =
        window.requestAnimationFrame(
          animateAudioHardware
        );
    }

    else {
      updateLEDs(0);

      player.animationFrame =
        null;
    }
  }


  function startAnalyzerAnimation() {
    const player =
      getPlayer();


    if (player.animationFrame) {
      return;
    }


    player.animationFrame =
      window.requestAnimationFrame(
        animateAudioHardware
      );
  }


  /* =========================================================
     TRACK INFORMATION
  ========================================================= */

  function updateTrackText() {
    const player =
      getPlayer();


    const ui =
      getUI();


    if (
      !ui ||
      !player.currentTrack
    ) {
      return;
    }


    const title =
      ui.querySelector(
        "[data-dt-player-title]"
      );


    const artist =
      ui.querySelector(
        "[data-dt-player-artist]"
      );


    if (title) {
      title.textContent =
        player.currentTrack.title ||
        "UNDER MY SKIN";
    }


    if (artist) {
      artist.textContent =
        player.currentTrack.artist ||
        "DISTANT TRAVELER";
    }
  }


  /* =========================================================
     PLAY STATE
  ========================================================= */

  function updatePlayState() {
    const player =
      getPlayer();


    const audio =
      player.audio;


    const ui =
      getUI();


    if (!ui) {
      return;
    }


    const playing =
      Boolean(
        audio.src &&
        !audio.paused
      );


    ui.classList.toggle(
      "is-playing",
      playing
    );


    /* MAIN PLAYER */

    const mainIcon =
      ui.querySelector(
        "[data-dt-main-play-icon]"
      );


    if (mainIcon) {
      mainIcon.textContent =
        playing
          ? "Ⅱ"
          : "▶";
    }


    /* HERO */

    const hero =
      getHeroButton();


    if (hero) {
      const label =
        hero.querySelector(
          "[data-dt-hero-play-label]"
        );


      if (
        label &&
        !label.dataset.defaultText
      ) {
        label.dataset.defaultText =
          label.textContent.trim();
      }


      const heroPlaying =
        player.currentButton === hero &&
        playing;


      hero.classList.toggle(
        "is-playing",
        heroPlaying
      );


      if (label) {
        label.textContent =
          heroPlaying
            ? "PAUSE"
            : (
                label.dataset.defaultText ||
                "PLAY NOW"
              );
      }
    }


    /* MUSIC ARCHIVE */

   getArchiveButtons().forEach((button) => {
  const active =
    button === player.currentButton &&
    playing;

  button.classList.toggle("is-playing", active);

  const icon = button.querySelector(".dt-archive-play-icon");
  if (icon) {
    icon.textContent = active ? "Ⅱ" : "▶";
  }

  const track = button.closest(".dt-archive-track");
  if (track) {
    track.classList.toggle("is-playing-track", active);
  }
});

if (!playing) {
  resetArchiveWaveforms();
}
    startAnalyzerAnimation();
  }


  /* =========================================================
     PROGRESS
  ========================================================= */

  function updateProgress() {
    const player =
      getPlayer();


    const audio =
      player.audio;


    const ui =
      getUI();


    if (!ui) {
      return;
    }


    const currentText =
      ui.querySelector(
        "[data-dt-current-time]"
      );


    const durationText =
      ui.querySelector(
        "[data-dt-duration]"
      );


    const progress =
      ui.querySelector(
        "[data-dt-progress]"
      );


    const current =
      audio.currentTime || 0;


    const duration =
      audio.duration || 0;


    if (currentText) {
      currentText.textContent =
        formatTime(current);
    }


    if (durationText) {
      durationText.textContent =
        formatTime(duration);
    }


    if (
      progress &&
      Number.isFinite(duration) &&
      duration > 0
    ) {
      const percentage =
        (
          current /
          duration
        ) *
        100;


      progress.value =
        percentage;


      progress.style.setProperty(
        "--dt-progress",
        `${percentage}%`
      );
    }
  }


  /* =========================================================
     LOAD TRACK
  ========================================================= */

  async function loadTrack({
    button,
    url,
    title,
    artist =
      "DISTANT TRAVELER",
    index = -1
  }) {
    if (!url) {
      console.warn(
        "Distant Traveler: MP3 URL missing."
      );

      return;
    }


    const player =
      getPlayer();


    const audio =
      player.audio;


    await ensureAnalyser();


    const normalized =
      normalizeURL(url);


    const current =
      audio.src
        ? normalizeURL(
            audio.src
          )
        : "";


    player.currentButton =
      button;


    player.currentIndex =
      index;


    player.currentTrack = {
      url:
        normalized,

      title:
        title ||
        "UNTITLED",

      artist:
        artist ||
        "DISTANT TRAVELER",

      index
    };


    updateTrackText();


    if (
      current !==
      normalized
    ) {
      audio.src =
        normalized;

      audio.load();
    }


    try {
      await audio.play();

    } catch (error) {
      console.error(
        "Distant Traveler playback error:",
        error
      );
    }
  }


  /* =========================================================
     TOGGLE TRACK
  ========================================================= */

  async function toggleTrack(
    button,
    index = -1
  ) {
    if (!button) {
      return;
    }


    const url =
      button.dataset.audio;


    if (!url) {
      console.warn(
        "Distant Traveler: this track has no MP3 URL."
      );

      return;
    }


    const player =
      getPlayer();


    const audio =
      player.audio;


    await ensureAnalyser();


    /*
      Same song:
      pause or resume.
    */

    if (
      player.currentButton === button &&
      audio.src
    ) {
      if (audio.paused) {
        try {
          await audio.play();

        } catch (error) {
          console.error(error);
        }
      }

      else {
        audio.pause();
      }


      return;
    }


    await loadTrack({
      button,

      url,

      title:
        button.dataset.title ||
        "UNTITLED",

      artist:
        button.dataset.artist ||
        "DISTANT TRAVELER",

      index
    });
  }


  /* =========================================================
     NEXT TRACK
  ========================================================= */

  async function playNextTrack() {
    const player =
      getPlayer();


    const tracks =
      getPlayableArchiveButtons();


    if (!tracks.length) {
      return;
    }


    const currentIndex =
      tracks.indexOf(
        player.currentButton
      );


    let nextIndex = 0;


    if (
      player.shuffleOn &&
      tracks.length > 1
    ) {
      do {
        nextIndex =
          Math.floor(
            Math.random() *
            tracks.length
          );

      } while (
        nextIndex ===
        currentIndex
      );
    }

    else {
      nextIndex =
        currentIndex < 0
          ? 0
          : (
              currentIndex + 1
            ) %
            tracks.length;
    }


    const button =
      tracks[nextIndex];


    await toggleTrack(
      button,
      getArchiveButtons()
        .indexOf(button)
    );
  }


  /* =========================================================
     PREVIOUS TRACK
  ========================================================= */

  async function playPreviousTrack() {
    const player =
      getPlayer();


    const audio =
      player.audio;


    /*
      Restart current song first.
    */

    if (
      audio.src &&
      audio.currentTime > 3
    ) {
      audio.currentTime =
        0;

      return;
    }


    const tracks =
      getPlayableArchiveButtons();


    if (!tracks.length) {
      return;
    }


    let currentIndex =
      tracks.indexOf(
        player.currentButton
      );


    if (currentIndex < 0) {
      currentIndex =
        0;
    }


    const previousIndex =
      currentIndex === 0
        ? tracks.length - 1
        : currentIndex - 1;


    const button =
      tracks[previousIndex];


    await toggleTrack(
      button,
      getArchiveButtons()
        .indexOf(button)
    );
  }


  /* =========================================================
     AUDIO EVENTS
  ========================================================= */

  function bindAudioEvents() {
    const player =
      getPlayer();


    if (player.audioEventsBound) {
      return;
    }


    player.audioEventsBound =
      true;


    const audio =
      player.audio;


    audio.addEventListener(
      "play",
      async () => {
        await ensureAnalyser();

        updatePlayState();

        startAnalyzerAnimation();
      }
    );


    audio.addEventListener(
      "pause",
      () => {
        updatePlayState();

        startAnalyzerAnimation();
      }
    );


    audio.addEventListener(
      "timeupdate",
      updateProgress
    );


    audio.addEventListener(
      "loadedmetadata",
      updateProgress
    );


    audio.addEventListener(
      "durationchange",
      updateProgress
    );


    audio.addEventListener(
      "ended",
      async () => {
        if (player.repeatOn) {
          audio.currentTime =
            0;


          try {
            await audio.play();

          } catch (error) {
            console.error(error);
          }


          return;
        }


        await playNextTrack();

        updatePlayState();
      }
    );


    audio.addEventListener(
      "error",
      () => {
        console.error(
          "Distant Traveler MP3 error:",
          audio.error,
          audio.src
        );
      }
    );
  }


  /* =========================================================
     PLAYER CONTROLS
  ========================================================= */

  function bindPlayerControls() {
    const ui =
      getUI();


    if (!ui) {
      return;
    }


    if (
      ui.dataset.dtPlayerVersion ===
      VERSION
    ) {
      return;
    }


    ui.dataset.dtPlayerVersion =
      VERSION;


    const player =
      getPlayer();


    const audio =
      player.audio;


    const mainPlay =
      ui.querySelector(
        "[data-dt-main-play]"
      );


    const previous =
      ui.querySelector(
        "[data-dt-prev]"
      );


    const next =
      ui.querySelector(
        "[data-dt-next]"
      );


    const shuffle =
      ui.querySelector(
        "[data-dt-shuffle]"
      );


    const repeat =
      ui.querySelector(
        "[data-dt-repeat]"
      );


    const heart =
      ui.querySelector(
        "[data-dt-heart]"
      );


    const progress =
      ui.querySelector(
        "[data-dt-progress]"
      );


    /*
      New markup:
      data-dt-meter-color

      Older markup:
      data-dt-volume

      Both now control COLOR only.
    */

    const colorControl =
      ui.querySelector(
        "[data-dt-meter-color], [data-dt-volume]"
      );


    /* =====================================================
       MAIN PLAY
    ===================================================== */

    mainPlay?.addEventListener(
      "click",
      async () => {
        await ensureAnalyser();


        if (!audio.src) {
          /*
            Hero track first.
          */

          const hero =
            getHeroButton();


          if (
            hero &&
            hero.dataset.audio
          ) {
            await toggleTrack(
              hero,
              -1
            );

            return;
          }


          /*
            Otherwise first archive track.
          */

          const firstTrack =
            getPlayableArchiveButtons()[0];


          if (firstTrack) {
            await toggleTrack(
              firstTrack,
              0
            );
          }


          return;
        }


        if (audio.paused) {
          try {
            await audio.play();

          } catch (error) {
            console.error(error);
          }
        }

        else {
          audio.pause();
        }
      }
    );


    /* PREVIOUS */

    previous?.addEventListener(
      "click",
      playPreviousTrack
    );


    /* NEXT */

    next?.addEventListener(
      "click",
      playNextTrack
    );


    /* SHUFFLE */

    shuffle?.addEventListener(
      "click",
      () => {
        player.shuffleOn =
          !player.shuffleOn;


        shuffle.classList.toggle(
          "is-active",
          player.shuffleOn
        );
      }
    );


    /* REPEAT */

    repeat?.addEventListener(
      "click",
      () => {
        player.repeatOn =
          !player.repeatOn;


        repeat.classList.toggle(
          "is-active",
          player.repeatOn
        );
      }
    );


    /* HEART */

    heart?.addEventListener(
      "click",
      () => {
        const active =
          heart.classList.toggle(
            "is-active"
          );


        heart.textContent =
          active
            ? "♥"
            : "♡";
      }
    );


    /* SEEK */

    progress?.addEventListener(
      "input",
      () => {
        if (
          !Number.isFinite(
            audio.duration
          ) ||
          audio.duration <= 0
        ) {
          return;
        }


        const percentage =
          Number(
            progress.value
          );


        audio.currentTime =
          (
            percentage /
            100
          ) *
          audio.duration;


        progress.style.setProperty(
          "--dt-progress",
          `${percentage}%`
        );
      }
    );


    /* COLOR KNOB */

    if (colorControl) {
      setMeterColor(
        colorControl.value
      );


      colorControl.addEventListener(
        "input",
        () => {
          setMeterColor(
            colorControl.value
          );
        }
      );


      colorControl.addEventListener(
        "change",
        () => {
          setMeterColor(
            colorControl.value
          );
        }
      );
    }

    else {
      setMeterColor(0);
    }


    shuffle?.classList.toggle(
      "is-active",
      player.shuffleOn
    );


    repeat?.classList.toggle(
      "is-active",
      player.repeatOn
    );


    updateLEDs(0);

    redrawMeters();

    updateTrackText();

    updatePlayState();

    updateProgress();
  }


  /* =========================================================
     HERO + ARCHIVE GLOBAL CLICK HANDLER

     The bottom player owns ALL music.
  ========================================================= */

  function installGlobalTrackHandler() {
    if (
      window.__DT_GLOBAL_HANDLER_VERSION ===
      VERSION
    ) {
      return;
    }


    window.__DT_GLOBAL_HANDLER_VERSION =
      VERSION;


    document.addEventListener(
      "click",
      async event => {
        /*
          Old cached player versions
          become inert.
        */

        if (
          window.__DT_ACTIVE_PLAYER_VERSION !==
          VERSION
        ) {
          return;
        }


        const target =
          event.target;


        if (
          !(target instanceof Element)
        ) {
          return;
        }


        /* HERO */

        const hero =
          target.closest(
            "[data-dt-hero-play]"
          );


        if (hero) {
          event.preventDefault();

          event.stopImmediatePropagation();


          await toggleTrack(
            hero,
            -1
          );


          return;
        }


        /* MUSIC ARCHIVE */

        const archive =
          target.closest(
            "[data-dt-archive-play]"
          );


        if (archive) {
          event.preventDefault();

          event.stopImmediatePropagation();


          const index =
            getArchiveButtons()
              .indexOf(
                archive
              );


          await toggleTrack(
            archive,
            index
          );
        }
      },
      true
    );
  }


  /* =========================================================
     BOOT
  ========================================================= */

  function boot() {
    getPlayer();

    bindAudioEvents();

    installGlobalTrackHandler();

    bindPlayerControls();
  }


  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      {
        once: true
      }
    );
  }

  else {
    boot();
  }


  /* =========================================================
     SHOPIFY THEME EDITOR
  ========================================================= */

  document.addEventListener(
    "shopify:section:load",
    () => {
      window.setTimeout(
        () => {
          bindPlayerControls();

          redrawMeters();

          updateTrackText();

          updatePlayState();

          updateProgress();
        },
        0
      );
    }
  );

})();